import React, { useEffect, memo } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import { Alert, Button } from "@mui/material";
import { removeItem } from "../../utils/LocalStorage";

const areEqual = (prevProps, nextProps) => {
    return (
        JSON.stringify(prevProps.toastList) === JSON.stringify(nextProps.toastList) &&
        prevProps.btnRequired === nextProps.btnRequired
    );
};

/**
 * Enterprise-grade Toast Component with Material UI and Notistack
 * Displays notifications with various severity levels
 */
const Toast = memo(
    ({
        toastList = [],
        position = "top-right",
        autoDelete = true,
        dismissTime = 3000,
        btnRequired = false,
        btnCallback,
        btnText = "Show",
    }) => {
        const { enqueueSnackbar, closeSnackbar } = useSnackbar();

        const parsePosition = (pos) => {
            const positions = {
                "top-right": { vertical: "top", horizontal: "right" },
                "top-left": { vertical: "top", horizontal: "left" },
                "top-center": { vertical: "top", horizontal: "center" },
                "bottom-right": { vertical: "bottom", horizontal: "right" },
                "bottom-left": { vertical: "bottom", horizontal: "left" },
                "bottom-center": { vertical: "bottom", horizontal: "center" },
            };
            return positions[pos] || positions["top-right"];
        };

        const getSeverity = (toast) => {
            const bgColor = toast.backgroundColor?.toLowerCase() || "";
            if (bgColor.includes("red") || bgColor.includes("error")) return "error";
            if (bgColor.includes("green") || bgColor.includes("success")) return "success";
            if (bgColor.includes("yellow") || bgColor.includes("warning")) return "warning";
            if (bgColor.includes("blue") || bgColor.includes("info")) return "info";
            return "info";
        };

        useEffect(() => {
            toastList.forEach((toast) => {
                const severity = getSeverity(toast);
                const anchorOrigin = parsePosition(position);

                const handleDelete = (key) => {
                    if (window.location.pathname === "/") {
                        removeItem("ad_module");
                    }
                    closeSnackbar(key);
                };

                const handleButtonClick = (key) => {
                    closeSnackbar(key);
                    if (btnCallback) btnCallback();
                };

                enqueueSnackbar(toast.title, {
                    key: toast.id,
                    variant: severity,
                    anchorOrigin,
                    autoHideDuration: autoDelete ? dismissTime : null,
                    content: (key) => (
                        <Alert
                            severity={severity}
                            onClose={() => handleDelete(key)}
                            sx={{
                                width: "100%",
                                minWidth: 300,
                                boxShadow: 3,
                            }}
                            action={
                                btnRequired ? (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleButtonClick(key)}
                                    >
                                        {btnText}
                                    </Button>
                                ) : null
                            }
                        >
                            {toast.description || toast.title}
                        </Alert>
                    ),
                });
            });
        }, [toastList, position, autoDelete, dismissTime, btnRequired, btnCallback, btnText]);

        return null; // Notistack handles the rendering
    },
    areEqual,
);

Toast.propTypes = {
    toastList: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            title: PropTypes.string.isRequired,
            icon: PropTypes.string,
            backgroundColor: PropTypes.string,
        }),
    ).isRequired,
    position: PropTypes.oneOf([
        "top-right",
        "top-left",
        "top-center",
        "bottom-right",
        "bottom-left",
        "bottom-center",
    ]),
    autoDelete: PropTypes.bool,
    dismissTime: PropTypes.number,
    btnRequired: PropTypes.bool,
    btnCallback: PropTypes.func,
    btnText: PropTypes.string,
};

Toast.displayName = "Toast";

export default Toast;
